import React from 'react';
import { useAppConfigContext } from '../../../../contexts/AppConfigProvider';
import { IApiConfig, useApi } from '../../../../hooks/useApi';
import { useUser } from '../../../../hooks/useUser';
import {
    DeleteBlockedReason,
    DeleteEligibilityResponse,
    DeleteState,
} from '../identityProviderManagement.types';

export interface UseDeleteIdentityProviderResult {
    state: DeleteState;
    /** Check eligibility. Must run before any confirmation is shown. */
    check: (idpId: string) => void;
    /** Perform the delete. Only meaningful once state is CONFIRMABLE. */
    confirm: () => void;
    reset: () => void;
    idpId?: string;
    blockedReason?: DeleteBlockedReason;
    usersAssignedCount?: number;
}

/**
 * Two-call delete, deliberately. The eligibility check is where the explanation
 * comes from — it can name how many users are still assigned. The delete call
 * enforces the same guardrails again and returns no detail, so a client that
 * skipped the check cannot talk its way past them. This hook therefore never
 * offers a confirmation for something already refused, and still copes when the
 * delete refuses anyway.
 */
const isNotFound = (e: { response?: { status?: number } } | null): boolean =>
    e?.response?.status === 404;

export const useDeleteIdentityProvider =
    (): UseDeleteIdentityProviderResult => {
        const { api } = useAppConfigContext();
        const user = useUser();
        const tenantId = user?.tenantIdentifier;

        const [state, setState] = React.useState<DeleteState>(DeleteState.IDLE);
        const [idpId, setIdpId] = React.useState<string>();
        const [blockedReason, setBlockedReason] =
            React.useState<DeleteBlockedReason>();
        const [usersAssignedCount, setUsersAssignedCount] =
            React.useState<number>();

        const base = `${api?.authProv}/v1/data/tenants/${tenantId}/identityProviders`;

        const eligibilityConfig = React.useMemo<IApiConfig>(
            () => ({
                url: `${base}/${idpId}/deleteEligibility`,
                method: 'GET',
                useAuth: true,
                executeType: 'manual',
                useCache: false,
            }),
            [base, idpId],
        );
        const {
            data: eligibility,
            error: checkError,
            execute: executeCheck,
        } = useApi<DeleteEligibilityResponse>(eligibilityConfig);

        const deleteConfig = React.useMemo<IApiConfig>(
            () => ({
                url: `${base}/${idpId}`,
                method: 'DELETE',
                useAuth: true,
                executeType: 'manual',
                useCache: false,
            }),
            [base, idpId],
        );
        const {
            loading: deleteLoading,
            error: deleteError,
            execute: executeDelete,
        } = useApi<void>(deleteConfig);

        /**
         * A successful delete is 202 with an empty body, so there is no response
         * data to key completion off. Watch the request finishing instead,
         * guarded by a flag so an unrelated render is not read as a result.
         */
        const deleteInFlight = React.useRef(false);

        /**
         * Set while a check is wanted, cleared when the admin closes the dialog.
         */
        const checkWanted = React.useRef(false);

        React.useEffect(() => {
            if (!checkError) return;
            if (!checkWanted.current) return;
            checkWanted.current = false;
            if (isNotFound(checkError)) {
                setState(DeleteState.ALREADY_DELETED);
                return;
            }
            setBlockedReason('CHECK_UNAVAILABLE');
            setUsersAssignedCount(undefined);
            setState(DeleteState.BLOCKED);
        }, [checkError]);

        React.useEffect(() => {
            if (!eligibility) return;
            if (!checkWanted.current) return;
            checkWanted.current = false;
            if (eligibility.eligible) {
                setBlockedReason(undefined);
                setUsersAssignedCount(undefined);
                setState(DeleteState.CONFIRMABLE);
            } else {
                setBlockedReason(eligibility.blockedReason);
                setUsersAssignedCount(eligibility.usersAssignedCount);
                setState(DeleteState.BLOCKED);
            }
        }, [eligibility]);

        React.useEffect(() => {
            if (!deleteInFlight.current || deleteLoading) return;
            deleteInFlight.current = false;
            if (!deleteError) {
                setState(DeleteState.DELETED);
                return;
            }
            setState(
                isNotFound(deleteError)
                    ? DeleteState.ALREADY_DELETED
                    : DeleteState.DELETE_REFUSED,
            );
        }, [deleteLoading, deleteError]);

        const check = React.useCallback(
            (id: string) => {
                checkWanted.current = true;
                setIdpId(id);
                setBlockedReason(undefined);
                setUsersAssignedCount(undefined);
                setState(DeleteState.CHECKING);
                executeCheck({
                    url: `${base}/${id}/deleteEligibility`,
                });
            },
            [base, executeCheck],
        );

        const confirm = React.useCallback(() => {
            if (!idpId) return;
            setState(DeleteState.DELETING);
            deleteInFlight.current = true;
            executeDelete({ url: `${base}/${idpId}` });
        }, [base, executeDelete, idpId]);

        const reset = React.useCallback(() => {
            checkWanted.current = false;
            setState(DeleteState.IDLE);
            setIdpId(undefined);
            setBlockedReason(undefined);
            setUsersAssignedCount(undefined);
        }, []);

        return {
            state,
            check,
            confirm,
            reset,
            idpId,
            blockedReason,
            usersAssignedCount,
        };
    };
